import {isEmpty, map, take} from 'lodash';
import {Fragment, useCallback, useMemo, useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {ClientDynamicComponentsConfig, DynamicComponentNames} from 'constants/client';
import {TRANSLATION_KEYS} from 'locale';
import {buildTranslationKey} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.WHITE_LABEL;

type Props = {
    name: DynamicComponentNames;
};

export const ComponentDescription = ({name}: Props) => {
    const {t} = useTranslation();
    const translationKey = useMemo(() => buildTranslationKey(name), [name]);
    const [collapsed, setCollapsed] = useState<boolean>(true);

    const units = useMemo(() => {
        const unitObject = ClientDynamicComponentsConfig[name]?.units || {};
        const list = Object.values(unitObject);
        return collapsed ? take(list, 2) : list;
    }, [name, collapsed]);

    const getUnitDescription = useCallback((unit: string) => {
        const unitKey = buildTranslationKey(unit as string);
        const componentUnitsKey = T_KEYS.COMPONENTS?.[translationKey]?.UNITS?.[unitKey];
        const commonUnitsKey = T_KEYS.COMMON_UNITS[unitKey];
        return t(componentUnitsKey || commonUnitsKey);
    }, [t, translationKey]);

    useEffect(() => setCollapsed(true), [name]);

    return (
        <div className='tw-my-2'>
            <p className='tw-text-default tw-mt-0'>{t(T_KEYS.COMPONENTS?.[translationKey]?.DESCRIPTION)}</p>
            {!isEmpty(units) &&
                <Fragment>
                    <div className='tw-ts-base-bold tw-mb-1 tw-mt-2'>{t(T_KEYS.UNIT_LABEL)}</div>
                    {map(units, (unit: string, index) => {
                        const description = getUnitDescription(unit);
                        const fullLabel = `— "${unit}" - ${description}`;
                        return (
                            <div key={index} className='tw-text-default'>
                                {fullLabel}
                            </div>
                        );
                    })}
                    <div
                        className='tw-inline-block tw-my-2 tw-text-primary tw-cursor-pointer tw-font-bold'
                        onClick={() => setCollapsed((prev) => !prev)}
                    >
                        {collapsed ? t(T_KEYS.TOGGLE.SHOW_MORE) : t(T_KEYS.TOGGLE.SHOW_LESS)}
                    </div>
                </Fragment>}
        </div>
    );
};